import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { preloadImage, getImageUrl } from '../utils/imageUtils';
import { isInWebView, openInSystemBrowser } from '../utils/webViewDetector';
import { isTWAEnvironment } from '../utils/platformDetection';
import '../styles/Auth.css';
import { FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import IconWrapper from './IconWrapper';

const Login: React.FC = () => {
    const { t } = useI18n();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [showGuestConfirm, setShowGuestConfirm] = useState(false);
    const [useBackupBg, setUseBackupBg] = useState(false);
    const [isWebView, setIsWebView] = useState(false);

    const backgroundImageUrl = useBackupBg
        ? getImageUrl('/background.svg')
        : getImageUrl('/background_login.png');

    const { loginAsGuest, loginWithGoogle, currentUser } = useAuth();
    const navigate = useNavigate();

    // 若已登入直接導向首頁
    useEffect(() => {
        if (currentUser) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    // 登入頁進入時若在 WebView（非 TWA）則嘗試導向系統瀏覽器，並記錄狀態供 UI 顯示提示
    useEffect(() => {
        const webViewDetected = isInWebView();
        const isTWA = isTWAEnvironment();
        setIsWebView(webViewDetected);
        if (webViewDetected && !isTWA) {
            openInSystemBrowser();
        }
    }, []);

    useEffect(() => {
        preloadImage(getImageUrl('/background_login.png')).then((ok) => {
            if (!ok) setUseBackupBg(true);
        });
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleGuestLogin = async () => {
        try {
            setError('');
            setLoading(true);
            await loginAsGuest();
        } catch (err: any) {
            setError(err.message || t('login.failed'));
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setError('');
            setLoading(true);
            await loginWithGoogle();
        } catch (_) {
            setLoading(false);
        }
    };

    const confirmGuestLogin = async () => {
        setShowGuestConfirm(false);
        await handleGuestLogin();
    };

    return (
        <div
            className="cv-auth-container"
            style={{
                backgroundImage: `url(${backgroundImageUrl})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center bottom',
                backgroundColor: '#e0f2f1',
            }}
        >
            <div className="cv-auth-left" style={{
                marginTop: windowWidth <= 480 ? '-10vh' :
                    windowWidth <= 768 ? '-15vh' : '-35vh'
            }}>
                <div className="cv-auth-logo">
                    <h1 className="cv-logo-text">{t('app.appName').toUpperCase()}</h1>
                </div>
                <div className="cv-auth-platform">
                    <h2>{t('login.readyToStart')}</h2>
                    <p>{t('login.joinUs')}</p>
                </div>
            </div>

            <div className="cv-auth-right">
                <div className="cv-auth-form">
                    <h2>{loading ? t('login.loggingIn') : t('login.welcome')}</h2>
                    {error && <div className="cv-auth-error">{error}</div>}

                    {/* WebView 提示：未跳轉時顯示，對齊 TaskyVenture */}
                    {isWebView && !isTWAEnvironment() && (
                        <div className="cv-auth-webview-warning" role="alert">
                            <IconWrapper icon={FaExclamationTriangle} className="cv-auth-webview-warning-icon" />
                            <div>
                                <p className="cv-auth-webview-warning-title">{t('login.webviewWarningTitle')}</p>
                                <p className="cv-auth-webview-warning-desc">{t('login.webviewWarningDesc')}</p>
                            </div>
                        </div>
                    )}

                    <div className="cv-auth-methods">
                        {isWebView && !isTWAEnvironment() ? (
                            <button
                                type="button"
                                className="cv-social-btn cv-google-btn google-auth-button"
                                style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                onClick={() => setError(t('login.webviewGoogleDisabled'))}
                                aria-disabled
                            >
                                <img src={getImageUrl('/google.png')} alt="Google" className="cv-google-icon" />
                                <span>{t('login.googleLogin')}</span>
                            </button>
                        ) : (
                            <button
                                className="cv-social-btn cv-google-btn google-auth-button"
                                onClick={handleGoogleLogin}
                                disabled={loading}
                            >
                                <img src={getImageUrl('/google.png')} alt="Google" className="cv-google-icon" />
                                <span>{t('login.googleLogin')}</span>
                            </button>
                        )}

                        <div className="cv-auth-divider">
                            <span>{t('app.or')}</span>
                        </div>
                    </div>

                    <div className="cv-auth-register">
                        <button
                            type="button"
                            className="cv-guest-link"
                            onClick={() => setShowGuestConfirm(true)}
                            disabled={loading}
                        >
                            {t('login.guest')}
                        </button>
                    </div>
                </div>
            </div>

            {showGuestConfirm && (
                <div className="cv-guest-modal-overlay">
                    <div className="cv-guest-modal-content">
                        <div className="cv-guest-modal-header">
                            <h3>{t('login.guestConfirmTitle')}</h3>
                        </div>
                        <div className="cv-guest-modal-body">
                            <div className="cv-guest-modal-warning">
                                <IconWrapper icon={FaInfoCircle} className="cv-guest-modal-warning-icon" />
                                <strong>{t('login.guestConfirmImportant')}</strong><br />
                                {t('login.guestConfirmNote')}
                            </div>
                            <p className="cv-guest-modal-question">
                                {t('login.guestConfirmQuestion')}
                            </p>
                        </div>
                        <div className="cv-guest-modal-footer">
                            <button
                                type="button"
                                className="cv-guest-modal-btn cv-guest-modal-btn-cancel"
                                onClick={() => setShowGuestConfirm(false)}
                            >
                                {t('login.cancel')}
                            </button>
                            <button
                                type="button"
                                className="cv-guest-modal-btn cv-guest-modal-btn-confirm"
                                onClick={confirmGuestLogin}
                                disabled={loading}
                            >
                                {loading ? t('login.loggingIn') : t('login.confirmLogin')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
